import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Mail, RefreshCw } from 'lucide-react';
import type {
  InboxMessageDetail,
  InboxMessageSummary,
  MailboxResponse,
  SendMessageResponse,
} from '@antigravity/types';
import { ComposeModal } from '../components/webmail/ComposeModal';
import { ConnectSenderModal } from '../components/campaigns/ConnectSenderModal';
import { CreateMailboxModal } from '../components/webmail/CreateMailboxModal';
import { ManageMailboxesModal } from '../components/webmail/ManageMailboxesModal';
import { MessageDetail } from '../components/webmail/MessageDetail';
import { MessageList } from '../components/webmail/MessageList';
import { WebmailSidebar } from '../components/webmail/WebmailSidebar';
import { useAuth } from '../context/AuthContext';
import { apiClient, ApiClientError } from '../services/api-client';

export const WebmailPage: FC = () => {
  const { currentOrg } = useAuth();

  // State: Mailboxes
  const [mailboxes, setMailboxes] = useState<MailboxResponse[]>([]);
  const [selectedMailbox, setSelectedMailbox] = useState<MailboxResponse | null>(null);
  const [isLoadingMailboxes, setIsLoadingMailboxes] = useState(true);
  const [mailboxError, setMailboxError] = useState<string | null>(null);

  // State: Messages & Folders
  const [activeFolder, setActiveFolder] = useState<string>('inbox');
  const [messages, setMessages] = useState<InboxMessageSummary[]>([]);
  const [totalMessages, setTotalMessages] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);

  // State: Message Detail
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
  const [messageDetail, setMessageDetail] = useState<InboxMessageDetail | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [isMarkingRead, setIsMarkingRead] = useState(false);

  // State: Compose, Mailbox Provisioning & Settings Modals
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [isConnectSenderOpen, setIsConnectSenderOpen] = useState(false);
  const [isCreateMailboxOpen, setIsCreateMailboxOpen] = useState(false);
  const [isManageMailboxesOpen, setIsManageMailboxesOpen] = useState(false);
  const [mobilePane, setMobilePane] = useState<'folders' | 'list' | 'detail'>('list');

  // Load mailboxes for active organization
  const fetchMailboxes = useCallback(async () => {
    setIsLoadingMailboxes(true);
    setMailboxError(null);

    try {
      const res = await apiClient.listMailboxes({ limit: 50 });
      const items = res.items || [];
      setMailboxes(items);

      if (items.length > 0) {
        // Keep existing selected mailbox if still present, or pick first ACTIVE
        setSelectedMailbox((prev) => {
          if (prev && items.some((m) => m.id === prev.id)) return prev;
          const firstActive = items.find((m) => m.status === 'ACTIVE');
          return firstActive || items[0];
        });
      } else {
        setSelectedMailbox(null);
      }
    } catch (err: any) {
      if (err instanceof ApiClientError) {
        setMailboxError(err.message || 'Failed to load mailboxes');
      } else {
        setMailboxError('Unable to connect to mailbox service');
      }
    } finally {
      setIsLoadingMailboxes(false);
    }
  }, []);

  useEffect(() => {
    fetchMailboxes();
  }, [fetchMailboxes, currentOrg?.id]);

  // Load messages for the selected mailbox and active folder
  const fetchMessages = useCallback(
    async (currentPage = page, search = activeSearch, folder = activeFolder) => {
      if (!selectedMailbox) return;

      setIsLoadingMessages(true);
      setMessagesError(null);

      try {
        const res = await apiClient.getInboxMessages(selectedMailbox.id, {
          page: currentPage,
          limit: 20,
          search: search || undefined,
          folder,
        });

        setMessages(res.messages || []);
        setTotalMessages(res.total || 0);
        setTotalPages(res.totalPages || 1);
        setPage(res.page || 1);
      } catch (err: any) {
        if (err instanceof ApiClientError) {
          setMessagesError(err.message || 'Failed to load messages');
        } else {
          setMessagesError('Could not retrieve messages from IMAP server');
        }
      } finally {
        setIsLoadingMessages(false);
      }
    },
    [selectedMailbox, page, activeSearch, activeFolder],
  );

  useEffect(() => {
    if (selectedMailbox) {
      fetchMessages(1, '', activeFolder);
      setSelectedMessageId(null);
      setMessageDetail(null);
    }
  }, [selectedMailbox?.id, activeFolder]);

  const handleSelectFolder = (folder: string) => {
    setActiveFolder(folder);
    setSelectedMessageId(null);
    setMessageDetail(null);
    setPage(1);
    setMobilePane('list');
  };

  // Load message detail
  const handleSelectMessage = async (msgId: string) => {
    if (!selectedMailbox) return;

    setSelectedMessageId(msgId);
    setIsLoadingDetail(true);
    setDetailError(null);
    setMobilePane('detail');

    try {
      const detail = await apiClient.getMessageDetail(selectedMailbox.id, msgId, activeFolder);
      setMessageDetail(detail);

      // Auto-mark as read if unread
      if (!detail.isRead) {
        apiClient.markMessageRead(selectedMailbox.id, msgId, activeFolder).catch(() => {});
        setMessages((prev) =>
          prev.map((m) => (m.id === msgId ? { ...m, isRead: true } : m)),
        );
        setMessageDetail((prev) => (prev ? { ...prev, isRead: true } : prev));
      }
    } catch (err: any) {
      if (err instanceof ApiClientError) {
        setDetailError(err.message || 'Failed to load message detail');
      } else {
        setDetailError('Error retrieving message body');
      }
    } finally {
      setIsLoadingDetail(false);
    }
  };

  // Mark message as read manual action
  const handleMarkAsRead = async (msgId: string) => {
    if (!selectedMailbox) return;
    setIsMarkingRead(true);
    try {
      await apiClient.markMessageRead(selectedMailbox.id, msgId);
      setMessages((prev) =>
        prev.map((m) => (m.id === msgId ? { ...m, isRead: true } : m)),
      );
      if (messageDetail && messageDetail.id === msgId) {
        setMessageDetail({ ...messageDetail, isRead: true });
      }
    } catch (err: any) {
      console.warn('Failed to mark message as read:', err.message);
    } finally {
      setIsMarkingRead(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchMessages(newPage, activeSearch);
  };

  const handleSearchSubmit = () => {
    setActiveSearch(searchQuery);
    fetchMessages(1, searchQuery);
  };

  const handleMessageSent = (_res: SendMessageResponse) => {
    // Refresh inbox
    fetchMessages(1, activeSearch);
  };

  const unreadCount = messages.filter((m) => !m.isRead).length;

  return (
    <div className="h-full w-full flex flex-col bg-slate-950 overflow-hidden select-none">
      {/* Mobile Top Navigation Bar */}
      <div className="md:hidden flex items-center justify-between px-4 py-2.5 bg-slate-900 border-b border-slate-800">
        {mobilePane !== 'list' ? (
          <button
            type="button"
            onClick={() => setMobilePane('list')}
            className="flex items-center gap-1.5 text-xs text-brand-400 font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to list</span>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setMobilePane('folders')}
            className="text-xs text-slate-300 font-medium"
          >
            Folders & Mailbox
          </button>
        )}

        <div className="text-xs font-semibold text-white truncate max-w-[200px]">
          {selectedMailbox ? selectedMailbox.emailAddress : 'Webmail'}
        </div>

        <button
          type="button"
          onClick={() => setIsComposeOpen(true)}
          disabled={!selectedMailbox}
          className="text-xs text-brand-400 font-semibold disabled:opacity-40"
        >
          Compose
        </button>
      </div>

      {/* Main 3-Pane Responsive Layout */}
      {isLoadingMailboxes ? (
        <div className="flex-1 flex items-center justify-center text-slate-400 text-xs">
          <RefreshCw className="w-5 h-5 animate-spin mr-2 text-brand-400" />
          <span>Connecting to sovereign mailbox service...</span>
        </div>
      ) : mailboxError ? (
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center space-y-3">
          <p className="text-xs text-rose-400">{mailboxError}</p>
          <button
            type="button"
            onClick={fetchMailboxes}
            className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs text-white transition-colors"
          >
            Retry
          </button>
        </div>
      ) : mailboxes.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-slate-400 space-y-4">
          <div className="w-14 h-14 rounded-2xl bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-400">
            <Mail className="w-7 h-7" />
          </div>
          <div className="space-y-1 max-w-sm">
            <h3 className="text-base font-semibold text-white">No Mailboxes Provisioned</h3>
            <p className="text-xs text-slate-400">
              Your organization does not have any active mailboxes yet. Connect your custom domain or add a business email to start sending and receiving mail.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => setIsCreateMailboxOpen(true)}
              className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-500 text-white text-xs font-semibold shadow-lg shadow-brand-500/20 transition-all flex items-center gap-2 cursor-pointer"
            >
              <Mail className="w-4 h-4" />
              <span>+ Create Business Mailbox</span>
            </button>
            <button
              type="button"
              onClick={() => setIsConnectSenderOpen(true)}
              className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-xs font-semibold border border-slate-700 transition-all flex items-center gap-2 cursor-pointer"
            >
              <span>Connect External Domain / Sender (3 Ways)</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex overflow-hidden">
          {/* Pane 1: Folders & Mailbox Selector (Desktop + Mobile conditional) */}
          <div
            className={`h-full ${
              mobilePane === 'folders' ? 'block w-full' : 'hidden md:block'
            }`}
          >
            <WebmailSidebar
              mailboxes={mailboxes}
              selectedMailbox={selectedMailbox}
              onSelectMailbox={(mb) => {
                setSelectedMailbox(mb);
                setMobilePane('list');
              }}
              onOpenCompose={() => setIsComposeOpen(true)}
              onOpenConnectSender={() => setIsConnectSenderOpen(true)}
              onOpenCreateMailbox={() => setIsCreateMailboxOpen(true)}
              onOpenManageMailboxes={() => setIsManageMailboxesOpen(true)}
              unreadCount={unreadCount}
              activeFolder={activeFolder}
              onSelectFolder={handleSelectFolder}
            />
          </div>

          {/* Pane 2: Message List (Desktop + Mobile conditional) */}
          <div
            className={`h-full md:w-96 shrink-0 ${
              mobilePane === 'list' ? 'block w-full' : 'hidden md:block'
            }`}
          >
            <MessageList
              messages={messages}
              selectedMessageId={selectedMessageId}
              onSelectMessage={handleSelectMessage}
              isLoading={isLoadingMessages}
              error={messagesError}
              onRefresh={() => fetchMessages(page, activeSearch)}
              page={page}
              totalPages={totalPages}
              total={totalMessages}
              onPageChange={handlePageChange}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onSearchSubmit={handleSearchSubmit}
            />
          </div>

          {/* Pane 3: Message Detail View (Desktop + Mobile conditional) */}
          <div
            className={`h-full flex-1 ${
              mobilePane === 'detail' ? 'block w-full' : 'hidden md:block'
            }`}
          >
            <MessageDetail
              message={messageDetail}
              isLoading={isLoadingDetail}
              error={detailError}
              onMarkAsRead={handleMarkAsRead}
              isMarkingRead={isMarkingRead}
            />
          </div>
        </div>
      )}

      {/* Compose Modal */}
      {selectedMailbox && (
        <ComposeModal
          mailbox={selectedMailbox}
          isOpen={isComposeOpen}
          onClose={() => setIsComposeOpen(false)}
          onMessageSent={handleMessageSent}
        />
      )}

      {/* Connect Sender & Custom Domain Modal (3 Ways) */}
      <ConnectSenderModal
        isOpen={isConnectSenderOpen}
        onClose={() => setIsConnectSenderOpen(false)}
        onSuccess={(newMb) => {
          setMailboxes((prev) => [newMb, ...prev]);
          setSelectedMailbox(newMb);
          fetchMailboxes();
        }}
      />

      {/* Provision New Mailbox Modal */}
      <CreateMailboxModal
        isOpen={isCreateMailboxOpen}
        onClose={() => setIsCreateMailboxOpen(false)}
        onSuccess={(newMb) => {
          setMailboxes((prev) => [newMb, ...prev]);
          setSelectedMailbox(newMb);
          fetchMailboxes();
        }}
      />

      {/* Manage Mailboxes & Credentials Modal */}
      <ManageMailboxesModal
        isOpen={isManageMailboxesOpen}
        onClose={() => setIsManageMailboxesOpen(false)}
        mailboxes={mailboxes}
        onRefresh={fetchMailboxes}
        onOpenCreate={() => setIsCreateMailboxOpen(true)}
      />
    </div>
  );
};
